/**
 * The in-progress state of one room's entry form.
 *
 * Numeric fields are held as strings because the operator types into them
 * ("3." must survive a keystroke); every consumer goes through `toNumber` and
 * the amount maths lives here, not in the components, so the right rail, the
 * print tags and the saved order can never drift apart.
 */
import { serviceBySlug, itemPriceKey } from './serviceCatalogue'
import type { B2bOrder, B2bOrderItem, B2bRateCard } from './types'

export interface DraftItemLine {
  id: string
  itemName: string
  quantity: string
  unitPrice: string
}

export interface DraftOrder {
  id: string
  serviceSlug: string
  /** Weight-based fields. */
  weightKg: string
  ratePerKg: string
  pieces: string
  /** Item-based lines. */
  items: DraftItemLine[]
}

export interface DraftErrors {
  weightKg?: string
  ratePerKg?: string
  pieces?: string
  items?: Record<string, string>
  form?: string
}

export const toNumber = (value: string): number => {
  const parsed = Number(String(value).trim())
  return Number.isFinite(parsed) ? parsed : 0
}

let draftCounter = 0
const nextId = (prefix: string) => {
  draftCounter += 1
  return `${prefix}-${draftCounter}`
}

export const newDraftOrder = (serviceSlug: string, rateCard?: B2bRateCard): DraftOrder => ({
  id: nextId('draft'),
  serviceSlug,
  weightKg: '',
  ratePerKg: rateCard ? String(rateCard.washIronPerKg) : '',
  pieces: '',
  items: [],
})

export const newDraftItem = (
  serviceSlug: string,
  itemName: string,
  rateCard?: B2bRateCard,
): DraftItemLine => ({
  id: nextId('item'),
  itemName,
  quantity: '1',
  unitPrice: String(rateCard?.itemPrices[itemPriceKey(serviceSlug, itemName)] ?? ''),
})

const isWeightDraft = (draft: DraftOrder) =>
  serviceBySlug(draft.serviceSlug)?.entryType === 'weight'

export const draftAmount = (draft: DraftOrder): number => {
  if (isWeightDraft(draft)) {
    return Math.round(toNumber(draft.weightKg) * toNumber(draft.ratePerKg))
  }

  return draft.items.reduce(
    (sum, item) => sum + toNumber(item.quantity) * toNumber(item.unitPrice),
    0,
  )
}

/** Physical pieces - drives the tag sequence (FRD §7). */
export const draftPieces = (draft: DraftOrder): number => {
  if (isWeightDraft(draft)) return Math.max(0, Math.trunc(toNumber(draft.pieces)))
  return draft.items.reduce((sum, item) => sum + Math.max(0, Math.trunc(toNumber(item.quantity))), 0)
}

export const draftErrors = (draft: DraftOrder): DraftErrors => {
  const errors: DraftErrors = {}

  if (isWeightDraft(draft)) {
    if (toNumber(draft.weightKg) <= 0) errors.weightKg = 'Enter a weight above 0.'
    if (toNumber(draft.ratePerKg) <= 0) errors.ratePerKg = 'Enter a rate above 0.'
    if (Math.trunc(toNumber(draft.pieces)) < 1) errors.pieces = 'At least 1 piece.'
    return errors
  }

  if (draft.items.length === 0) {
    errors.form = 'Pick at least one item.'
    return errors
  }

  const itemErrors: Record<string, string> = {}
  draft.items.forEach((item) => {
    if (!item.itemName.trim()) {
      itemErrors[item.id] = 'Name this item.'
      return
    }
    if (Math.trunc(toNumber(item.quantity)) < 1) {
      itemErrors[item.id] = 'Quantity must be at least 1.'
      return
    }
    if (toNumber(item.unitPrice) <= 0) itemErrors[item.id] = 'Enter a price above 0.'
  })

  if (Object.keys(itemErrors).length > 0) errors.items = itemErrors

  return errors
}

export const hasDraftErrors = (errors: DraftErrors): boolean =>
  Boolean(errors.weightKg || errors.ratePerKg || errors.pieces || errors.form || errors.items)

export const draftToOrder = (
  draft: DraftOrder,
  roomId: string,
  cycleId: string,
  createdAt: string,
): B2bOrder => {
  const orderId = draft.id.startsWith('order-') ? draft.id : `order-${roomId}-${draft.serviceSlug}`

  const items: B2bOrderItem[] = isWeightDraft(draft)
    ? [
        {
          id: `${orderId}-0`,
          orderId,
          itemName: null,
          weightKg: toNumber(draft.weightKg),
          unitPrice: toNumber(draft.ratePerKg),
          quantity: Math.trunc(toNumber(draft.pieces)),
          amount: Math.round(toNumber(draft.weightKg) * toNumber(draft.ratePerKg)),
        },
      ]
    : draft.items.map((item, index) => ({
        id: `${orderId}-${index}`,
        orderId,
        itemName: item.itemName.trim(),
        weightKg: null,
        unitPrice: toNumber(item.unitPrice),
        quantity: Math.trunc(toNumber(item.quantity)),
        amount: Math.trunc(toNumber(item.quantity)) * toNumber(item.unitPrice),
      }))

  return { id: orderId, roomId, cycleId, serviceSlug: draft.serviceSlug, createdAt, items }
}

/** Re-open an already-billed room into the same form (FRD §8, last rule). */
export const orderToDraft = (order: B2bOrder): DraftOrder => {
  const service = serviceBySlug(order.serviceSlug)

  if (service?.entryType === 'weight') {
    const line = order.items[0]
    return {
      id: order.id,
      serviceSlug: order.serviceSlug,
      weightKg: line ? String(line.weightKg ?? '') : '',
      ratePerKg: line ? String(line.unitPrice ?? '') : '',
      pieces: line ? String(line.quantity ?? '') : '',
      items: [],
    }
  }

  return {
    id: order.id,
    serviceSlug: order.serviceSlug,
    weightKg: '',
    ratePerKg: '',
    pieces: '',
    items: order.items.map((item) => ({
      id: item.id,
      itemName: item.itemName ?? '',
      quantity: String(item.quantity),
      unitPrice: String(item.unitPrice),
    })),
  }
}
